// ricola-accordion: the shared <ric-accordion> custom element.
//
// Ports Ricola's Vue AccordionPanel + TransitionHeight to a framework-free
// custom element. Each panel is a `.AccordionPanel` containing a
// `.AccordionPanel--triggerWrapper` (title + `.AccordionPanel--icon` circle +
// a full-cover `<button class="AccordionPanel--trigger">`) and an
// `.AccordionPanel--content > .AccordionPanel--content--inner`.
//
// Smooth reveal (mirrors the original):
//   open  : measure scrollHeight -> set height:0 -> next frame -> height:<measured>
//           -> on transitionend -> height:"" (auto)
//   close : set height:<measured> (from auto) -> next frame -> height:0
// The `transition:height .25s ease-in-out` itself lives in faq-accordion.css.
//
// prefers-reduced-motion: skip the height animation (jump open/close).
// No-JS fallback: the section markup keeps content readable until this
// element upgrades and adds `is-js` to the host (see the `is-js` gate in CSS).

if (typeof window !== 'undefined' && window.customElements && !customElements.get('ric-accordion')) {
  const REDUCE = () => matchMedia('(prefers-reduced-motion: reduce)').matches;

  class RicAccordion extends HTMLElement {
    connectedCallback() {
      // Mark as upgraded so the no-JS fallback CSS can hand control to JS.
      this.classList.add('is-js');
      this.single = this.hasAttribute('data-single');

      this.panels = [...this.querySelectorAll('.AccordionPanel')].map((panel) => {
        const trigger = panel.querySelector('.AccordionPanel--trigger');
        const content = panel.querySelector('.AccordionPanel--content');
        return { panel, trigger, content };
      }).filter((p) => p.trigger && p.content);

      // Establish a clean initial state for every panel (collapsed unless the
      // markup opted in with .is-open). This converts the no-JS "always open"
      // fallback into a real collapsed/expanded state.
      for (const item of this.panels) {
        const open = item.panel.classList.contains('is-open');
        this._setState(item, open, true);
      }

      this._onClick = (e) => {
        const trigger = e.target.closest('.AccordionPanel--trigger');
        if (!trigger || !this.contains(trigger)) return;
        const item = this.panels.find((p) => p.trigger === trigger);
        if (!item) return;
        e.preventDefault();
        this._toggle(item);
      };
      this.addEventListener('click', this._onClick);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this._onClick);
      for (const item of this.panels || []) {
        if (item._onEnd) item.content.removeEventListener('transitionend', item._onEnd);
        if (item._raf) cancelAnimationFrame(item._raf);
      }
    }

    _toggle(item) {
      const willOpen = !item.panel.classList.contains('is-open');
      if (willOpen && this.single) {
        for (const other of this.panels) {
          if (other !== item && other.panel.classList.contains('is-open')) this._animate(other, false);
        }
      }
      this._animate(item, willOpen);
    }

    // Set the static (non-animated) ARIA/visibility state.
    _setState(item, open, instant) {
      item.panel.classList.toggle('is-open', open);
      item.trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
      item.content.setAttribute('aria-hidden', open ? 'false' : 'true');
      if (open) {
        item.content.removeAttribute('hidden');
      } else if (instant) {
        item.content.setAttribute('hidden', '');
      }
      if (instant) item.content.style.height = open ? '' : '0px';
    }

    _animate(item, open) {
      // Cancel any in-flight transition on this panel.
      if (item._onEnd) { item.content.removeEventListener('transitionend', item._onEnd); item._onEnd = null; }
      if (item._raf) { cancelAnimationFrame(item._raf); item._raf = 0; }

      item.trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
      item.content.setAttribute('aria-hidden', open ? 'false' : 'true');

      // prefers-reduced-motion (or no transition available): jump.
      if (REDUCE()) {
        item.panel.classList.toggle('is-open', open);
        if (open) { item.content.removeAttribute('hidden'); item.content.style.height = ''; }
        else { item.content.style.height = '0px'; item.content.setAttribute('hidden', ''); }
        return;
      }

      if (open) {
        item.panel.classList.add('is-open');
        item.content.removeAttribute('hidden');
        const target = item.content.scrollHeight;
        item.content.style.height = '0px';
        // Force reflow so the browser registers the 0px starting height.
        void item.content.offsetHeight;
        item._raf = requestAnimationFrame(() => {
          item._raf = 0;
          item.content.style.height = target + 'px';
        });
        item._onEnd = (e) => {
          if (e.propertyName !== 'height') return;
          item.content.removeEventListener('transitionend', item._onEnd);
          item._onEnd = null;
          // Settle to auto so the panel reflows with its content afterwards.
          if (item.panel.classList.contains('is-open')) item.content.style.height = '';
        };
        item.content.addEventListener('transitionend', item._onEnd);
      } else {
        // Closing: LOCK the current pixel height while still .is-open (so the
        // `.is-open .content{height:auto}` rule doesn't snap auto->0), THEN drop
        // .is-open, THEN animate the explicit height down to 0 next frame.
        const current = item.content.scrollHeight;
        item.content.style.height = current + 'px';
        void item.content.offsetHeight;            // reflow with the explicit height held
        item.panel.classList.remove('is-open');    // inline px height overrides — no jump
        item._raf = requestAnimationFrame(() => {
          item._raf = 0;
          item.content.style.height = '0px';        // transitions current -> 0 smoothly
        });
        item._onEnd = (e) => {
          if (e.propertyName !== 'height') return;
          item.content.removeEventListener('transitionend', item._onEnd);
          item._onEnd = null;
          if (!item.panel.classList.contains('is-open')) item.content.setAttribute('hidden', '');
        };
        item.content.addEventListener('transitionend', item._onEnd);
      }
    }
  }

  customElements.define('ric-accordion', RicAccordion);
}
